import { useMutation, useQuery, useQueryClient } from 'react-query';
import * as api from '../api/client';
import { useCurrentProvider } from '../lib/provider-store';
import type { PayDriversFilters, TransfersFilters } from '../types/repasses';

// Provedor do seletor (SRV-137) como ÚLTIMO segmento das keys de dados: trocar
// de provedor muda a key → refetch com o header novo, e os
// `invalidateQueries(['repasses', 'x'])` por prefixo continuam pegando tudo.
// `access` e `settings` ficam de fora (não são por provedor; `settings` é quem
// alimenta o seletor).
const useProviderKey = () => useCurrentProvider() ?? 'default';

const KEYS = {
  access: () => ['repasses', 'access'] as const,
  drivers: (f: PayDriversFilters, p: string) =>
    ['repasses', 'drivers', f, p] as const,
  balance: (p: string) => ['repasses', 'balance', p] as const,
  transfers: (f: TransfersFilters, p: string) =>
    ['repasses', 'transfers', f, p] as const,
};

// SEGURANÇA: visibilidade = usuário é brand_user (pay.brand_users).
export const useRepassesAccess = () =>
  useQuery(KEYS.access(), () => api.getAccess(), {
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

export const useRepassesDrivers = (filters: PayDriversFilters) => {
  const provider = useProviderKey();
  return useQuery(
    KEYS.drivers(filters, provider),
    () => api.listDrivers(filters),
    { keepPreviousData: true },
  );
};

export const useRepassesBalance = () => {
  const provider = useProviderKey();
  return useQuery(KEYS.balance(provider), () => api.getBalance());
};

// Refresh manual do painel: reinvalida entregadores + saldo (o botão de
// atualizar da Visão geral / Entregadores). Callback estável (não depende de
// filtros) — invalida a família inteira das duas queries.
export const useRefreshRepasses = () => {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries(['repasses', 'drivers']);
    qc.invalidateQueries(['repasses', 'balance']);
  };
};

export const useTransfers = (filters: TransfersFilters) => {
  const provider = useProviderKey();
  return useQuery(
    KEYS.transfers(filters, provider),
    () => api.listTransfers(filters),
    { keepPreviousData: true },
  );
};

/**
 * `enabled` existe pro sidebar: lá o gate "a marca tem credencial Asaas?"
 * (item da Verificação) só pode consultar `/settings` DEPOIS de saber que o
 * usuário é brand_user — o endpoint é só pra eles. Nas telas do Repasses o
 * default (`true`) vale, e todas compartilham esta query.
 */
export const useRepassesSettings = (options?: { enabled?: boolean }) =>
  useQuery(['repasses', 'settings'], () => api.getSettings(), {
    enabled: options?.enabled ?? true,
  });

export const useUpdateRepassesSettings = () => {
  const qc = useQueryClient();
  return useMutation(
    (dto: { withdrawal_day?: number }) => api.updateSettings(dto),
    { onSuccess: () => qc.invalidateQueries(['repasses', 'settings']) },
  );
};

// ── Detalhe do entregador ───────────────────────────────────────
export const useDriverDetail = (id: string) => {
  const provider = useProviderKey();
  return useQuery(
    ['repasses', 'driver', id, provider],
    () => api.getDriverDetail(id),
    { enabled: !!id },
  );
};

export const useDriverDeliveries = (id: string, params: any) => {
  const provider = useProviderKey();
  return useQuery(
    ['repasses', 'driver-deliveries', id, params, provider],
    () => api.getDriverDeliveries(id, params),
    { enabled: !!id, keepPreviousData: true },
  );
};

export const useUpdateBankAccount = (id: string) => {
  const qc = useQueryClient();
  return useMutation(
    (dto: Parameters<typeof api.updateDriverBankAccount>[1]) =>
      api.updateDriverBankAccount(id, dto),
    { onSuccess: () => qc.invalidateQueries(['repasses', 'driver', id]) },
  );
};

export const useManualPayments = (id: string) => {
  const provider = useProviderKey();
  return useQuery(
    ['repasses', 'manual-payments', id, provider],
    () => api.listManualPayments(id, { page: 1, limit: 50 }),
    { enabled: !!id },
  );
};

export const useManualPaymentDeliveries = (id: string, q: string) => {
  const provider = useProviderKey();
  return useQuery(
    ['repasses', 'manual-payment-deliveries', id, q, provider],
    () => api.searchManualPaymentDeliveries(id, { q: q || undefined, limit: 20 }),
    { enabled: !!id, staleTime: 30_000, keepPreviousData: true },
  );
};

export const useManualPaymentOrders = (driverId: string, paymentId: string | null) =>
  useQuery(
    ['repasses', 'manual-payment-orders', driverId, paymentId],
    () => api.getManualPaymentOrders(driverId, paymentId as string),
    { enabled: !!driverId && !!paymentId, staleTime: 60_000 },
  );

export const useCreateManualPayment = (id: string) => {
  const qc = useQueryClient();
  return useMutation(
    (dto: Parameters<typeof api.createManualPayment>[1]) =>
      api.createManualPayment(id, dto),
    {
      onSettled: () => {
        qc.invalidateQueries(['repasses', 'manual-payments', id]);
        qc.invalidateQueries(['repasses', 'manual-payment-deliveries', id]);
        qc.invalidateQueries(['repasses', 'driver', id]);
        qc.invalidateQueries(['repasses', 'balance']);
        qc.invalidateQueries(['repasses', 'transfers']);
      },
    },
  );
};

export const useRefreshBankStatus = (id: string) => {
  const qc = useQueryClient();
  return useMutation(() => api.refreshDriverBankStatus(id), {
    onSuccess: () => qc.invalidateQueries(['repasses', 'driver', id]),
  });
};

// ── KYC (verificação da conta da marca) ─────────────────────────
// SEM o provedor na key, ao contrário das outras: o KYC é SEMPRE da conta
// Asaas da marca (o pay-api resolve pela credencial, não pelo seletor), então
// trocar de carteira no topo não muda o que esta tela mostra nem justifica
// refetch. Os `invalidateQueries(['repasses', 'kyc-…'])` continuam pegando.
export const useKycStatus = () =>
  useQuery(['repasses', 'kyc-status'], () => api.getKycStatus(), {
    retry: false,
  });

export const useKycDocuments = () =>
  useQuery(['repasses', 'kyc-docs'], () => api.getKycDocuments(), {
    retry: false,
  });

const invalidateKyc = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries(['repasses', 'kyc-status']);
  qc.invalidateQueries(['repasses', 'kyc-docs']);
  // O status da conta também aparece nas Configurações e libera a recarga.
  qc.invalidateQueries(['repasses', 'settings']);
};

/**
 * Botão "Atualizar": repergunta o status à Asaas. A resposta já é o status
 * novo — ela entra direto no cache pra não gastar outra chamada (a Asaas
 * limita essas consultas a 10/min por subconta).
 */
export const useRefreshKyc = () => {
  const qc = useQueryClient();
  return useMutation(() => api.refreshKyc(), {
    onSuccess: (status) => {
      if (status) qc.setQueryData(['repasses', 'kyc-status'], status);
      invalidateKyc(qc);
    },
  });
};

export const useUploadKycDocument = () => {
  const qc = useQueryClient();
  return useMutation(
    (dto: Parameters<typeof api.uploadKycDocument>[0]) =>
      api.uploadKycDocument(dto),
    { onSuccess: () => invalidateKyc(qc) },
  );
};

// ── Mutations (ações) — invalidam as listas ao concluir ───────────
const invalidateAll = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries(['repasses', 'drivers']);
  qc.invalidateQueries(['repasses', 'balance']);
  qc.invalidateQueries(['repasses', 'transfers']);
  // Sem isso a linha da entrega e os totais do detalhe não atualizavam após
  // aprovar/pausar/negar (ficavam com os botões de ação apesar da ação feita).
  qc.invalidateQueries(['repasses', 'driver-deliveries']);
  qc.invalidateQueries(['repasses', 'driver']);
};

export const useDriverActions = () => {
  const qc = useQueryClient();
  const opts = { onSuccess: () => invalidateAll(qc) };
  return {
    approveTransfer: useMutation((driverId: string) => api.approveDriverTransfer(driverId), opts),
    approveDeliveries: useMutation((driverId: string) => api.approveDriverDeliveries(driverId), opts),
    pauseDeliveries: useMutation(
      (v: { driverId: string; reason?: string }) =>
        api.pauseDriverDeliveries(v.driverId, v.reason),
      opts,
    ),
  };
};

export const useRetryTransfer = () => {
  const qc = useQueryClient();
  return useMutation((transferId: string) => api.retryTransfer(transferId), {
    onSuccess: () => invalidateAll(qc),
  });
};

export const usePaymentActions = () => {
  const qc = useQueryClient();
  const opts = { onSuccess: () => invalidateAll(qc) };
  return {
    pause: useMutation((v: { id: string; reason?: string }) => api.pausePayment(v.id, v.reason), opts),
    resume: useMutation((id: string) => api.resumePayment(id), opts),
    deny: useMutation((v: { id: string; reason?: string }) => api.denyPayment(v.id, v.reason), opts),
    approve: useMutation((id: string) => api.approvePayment(id), opts),
  };
};
